import { Box, Typography } from '@mui/material'
import React, { useContext, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DefaultFetchDeviceFilters, DurationTestStatus } from '@/api/definition'
import ConfirmDialog from '@/components/ConfirmDialogBox'
import DeviceSelectionTable from '@/components/DeviceSelectionTable'
import { layoutStyles } from '@/utils/theme'
import { DevicesInTestContext } from './DevicesInTestContext'
import { useDeviceTypeFilters } from '@/hooks/useTransalationForFilter'
import { DeviceData } from '@/hooks/useDeviceSelection'

const SelectDevicesToStopTestStep: React.FC = () => {
  const { t } = useTranslation(['app'])
  const context = useContext(DevicesInTestContext)

  if (!context) {
    throw new Error(
      'SelectDevicesToStopTestStep must be used within DevicesInTestContext',
    )
  }

  const { state, dispatch, onStopDurationTest } = context

  const [open, setOpen] = React.useState(false)
  const [confirmed, setConfirmed] = React.useState(false)

  // Memoized to prevent unnecessary re-renders
  const handleSelectionChange = useCallback(
    (newlySelected: DeviceData[], deselected: string[]) => {
      const currentSelected = new Map(
        state.selectedDevices.map((device) => [device.uid, device]),
      )

      for (const device of newlySelected) {
        currentSelected.set(device.uid, device)
      }

      for (const uid of deselected) {
        currentSelected.delete(uid)
      }

      dispatch({
        type: 'SET_SELECTED_DEVICES',
        payload: Array.from(currentSelected.values()),
      })
    },
    [state.selectedDevices, dispatch],
  )

  const handleStopClick = useCallback(() => {
    if (confirmed) {
      onStopDurationTest()
    } else {
      setOpen(true)
    }
  }, [confirmed, onStopDurationTest])

  const handleConfirm = useCallback(() => {
    setConfirmed(true)
    onStopDurationTest()
  }, [onStopDurationTest])

  const handleClose = useCallback(() => {
    setConfirmed(false)
  }, [])

  const filterConfig = useDeviceTypeFilters()

  // Memoized the selected device IDs
  const selectedDeviceIds = useMemo(
    () => state.selectedDevices.map((device) => device.uid),
    [state.selectedDevices],
  )

  // Memorized the device filters
  const deviceFilters = useMemo(
    () => ({
      ...DefaultFetchDeviceFilters,
      inService: true,
      selfTest: 0,
      durationTestStatus: [DurationTestStatus.DurationTestInProgress],
      transferStatus: DefaultFetchDeviceFilters.transferStatus,
    }),
    [],
  )

  return (
    <Box sx={{ ...layoutStyles.pageContainer, mt: 0 }}>
      <DeviceSelectionTable
        initialDevices={[]}
        rowCount={0}
        selectedDeviceIds={selectedDeviceIds}
        onSelectionChange={handleSelectionChange}
        onNext={handleStopClick}
        deviceFilters={deviceFilters}
        pickTestProgress={true}
        nextButtonProps={{
          text: t('device.durationTest.stopDurationTest'),
          tooltip: t('device.durationTest.stopDurationTest'),
          color: 'warning',
          variant: 'outlined',
          sx: {
            backgroundColor: '#9c27b0',
            color: '#fff',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#8e24aa',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              backgroundColor: '#9c27b0',
              color: '#fff',
              opacity: 0.65,
            },
          },
          disabled: state.selectedDevices.length === 0,
        }}
        filterConfig={filterConfig}
        sharingConfig={{
          shareFilters: true,
          shareSearch: true,
          enableFilters: true,
          enableSearch: true,
        }}
      />
      <ConfirmDialog
        open={open}
        setOpen={setOpen}
        onConfirm={handleConfirm}
        onCancel={handleClose}
        title={t('common:general.areYouSure')}
        confirmText={t('common:general.yes')}
        cancelText={t('common:general.cancel')}
      >
        <Typography variant="subtitle1">
          {t(
            'device.durationTest.stopDurationPrompt',
            'Are you sure you want to skip the duration test?',
          )}
        </Typography>
      </ConfirmDialog>
    </Box>
  )
}

export default SelectDevicesToStopTestStep
